import { useState, useMemo } from 'react';
import { Copy, Check } from 'lucide-react';
import { useAllTasks } from '../../hooks/useTasks';
import { useAllProgressUpdates } from '../../hooks/useProgressNotes';
import { DayGroup } from './DayGroup';
import { format, eachDayOfInterval } from 'date-fns';
import { computeDayActivities } from '../../lib/summary';
import { getWeekScope } from '../../lib/dateScope';
import { useAppStore } from '../../store';
export type { SubtaskEntry, DayActivity } from '../../lib/summary';

export function SummaryView() {
  const [copied, setCopied] = useState(false);
  const selectedDate = useAppStore((s) => s.selectedDate);

  const { weekStart, weekEnd } = useMemo(() => getWeekScope(selectedDate), [selectedDate]);
  const allDays = useMemo(
    () => eachDayOfInterval({ start: weekStart, end: weekEnd }),
    [weekStart, weekEnd]
  );
  const days = useMemo(() => [...allDays].reverse(), [allDays]);

  const allTasks = useAllTasks();
  const allProgressUpdates = useAllProgressUpdates();

  const activitiesByDay = useMemo(() => {
    const result = new Map<string, ReturnType<typeof computeDayActivities>>();
    for (const day of days) {
      const dateStr = format(day, 'yyyy-MM-dd');
      const activities = computeDayActivities(allTasks, allProgressUpdates, dateStr);
      if (activities.length > 0) {
        result.set(dateStr, activities);
      }
    }
    return result;
  }, [allTasks, allProgressUpdates, days]);

  const generateMarkdown = () => {
    let md = `# Week of ${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}\n\n`;
    for (const day of days) {
      const dateStr = format(day, 'yyyy-MM-dd');
      const activities = activitiesByDay.get(dateStr);
      if (!activities || activities.length === 0) continue;
      md += `## ${format(day, 'EEEE, MMM d')}\n\n`;
      for (const act of activities) {
        const suffix = act.parentCompleted ? ' ✅' : '';
        md += `- **${act.parentTask.title}**${suffix}\n`;
        for (const entry of act.entries) {
          md += `  - ${entry.task.title}: ${entry.update.progressBefore}% → ${entry.update.progressAfter}%`;
          if (entry.update.note) md += ` — ${entry.update.note}`;
          md += '\n';
        }
      }
      md += '\n';
    }
    return md;
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(generateMarkdown());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const hasAnyActivity = days.some((d) => activitiesByDay.has(format(d, 'yyyy-MM-dd')));

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-headline text-text-primary">Summary</h2>
          <p className="text-caption text-text-muted mt-0.5">Weekly activity and progress notes</p>
        </div>
        <button onClick={handleCopy} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-accent text-bg-base text-caption font-semibold hover:bg-accent-hover shadow-lg shadow-accent/20 transition-colors">
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copied!' : 'Export'}
        </button>
      </div>

      {/* Week calendar strip */}
      <div className="grid grid-cols-7 gap-2 mb-6">
        {allDays.map((day) => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const hasActivity = activitiesByDay.has(dateStr);
          return (
            <div key={dateStr} className={`text-center py-2.5 rounded-xl transition-colors ${
              hasActivity
                ? 'bg-accent/8 border border-accent/20'
                : 'bg-bg-surface border border-border-subtle'
            }`}>
              <span className="text-label text-text-muted block">{format(day, 'EEE')}</span>
              <span className={`text-title mt-0.5 block ${hasActivity ? 'text-accent' : 'text-text-muted'}`}>{format(day, 'd')}</span>
            </div>
          );
        })}
      </div>

      {/* Day groups */}
      {!hasAnyActivity ? (
        <div className="text-center py-20 text-text-muted">
          <p className="text-headline mb-2">No activity this week</p>
          <p className="text-body">Move subtasks on the track or add notes to see activity here.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {days.map((day) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const activities = activitiesByDay.get(dateStr);
            if (!activities || activities.length === 0) return null;
            return <DayGroup key={dateStr} date={day} activities={activities} />;
          })}
        </div>
      )}
    </div>
  );
}
